'use client'
import { getUserInvoices } from '@/eshop/queries'
import { useQuery } from '@tanstack/react-query'
import Loader from '../Loader'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Invoice } from '@/eshop/gql/graphql'
import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { Button } from '@/components/Button'
import { useFormatPrice } from '@/eshop/utils/price'
import Pagination from '../Pagination'
import { useDownloadInvoice } from './useDownloadInvoice'

export default function Invoices() {
    const [pageK2, setPageK2] = useState<string | null>(null)
    const [page, setPage] = useState(1)
    const [rowsInProgress, setRowsInProgress] = useState<string[]>([])

    const prevPage = useRef<string | null>(null)
    const nextPage = useRef<string | null>(null)

    const { data, isFetching, isFetched } = useQuery({
        queryKey: ['invoices', pageK2],
        queryFn: () => getUserInvoices()(pageK2, 20).toPromise(),
    })
    const pagedData = useMemo((): Invoice[] => {
        if (isFetching) {
            nextPage.current = null
            prevPage.current = null
            return []
        }

        if (data?.tag === 'Ok' && data.value) {
            const v = data.value
            nextPage.current = v.invoices.nextPage || null
            prevPage.current = v.invoices.previousPage || null
            return (v.invoices.records || []) as Invoice[]
        }
        return []
    }, [data, isFetching])

    const { t } = useTranslation()
    const k2 = useK2Setup()
    const formatPrice = useFormatPrice(k2.currencyCode, undefined, true)

    const { mutate: downloadOrderDocument } = useDownloadInvoice({
        onMutate: (rid) => {
            setRowsInProgress((state) => [...state, rid])
        },
        onSettled: (rid) => {
            setRowsInProgress((state) => state.filter((i) => i !== rid))
        },
    })

    return (
        <div>
            <Loader isLoading={isFetching && !isFetched}>
                <div>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>
                                    {t('eshop.usersettings.invoice.number')}
                                </th>
                                <th className="text-right">
                                    {t('eshop.usersettings.invoice.value')}{' '}
                                    <span className="text-xs font-normal">
                                        ({t('eshop.price.baseSuffix')})
                                    </span>
                                </th>
                                <th className="text-right">
                                    {t('eshop.usersettings.invoice.value')}{' '}
                                    <span className="text-xs font-normal">
                                        ({t('eshop.price.vatSuffix')})
                                    </span>
                                </th>
                                <th>{t('eshop.usersettings.invoice.state')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedData.length === 0 && (
                                <tr>
                                    <td colSpan={5}>
                                        <div className="text-center">
                                            {t(
                                                'eshop.usersettings.invoice.noInvoices'
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {pagedData?.map((i) => (
                                <tr key={i?.RID} className="text-base">
                                    <td>{i?.documentIdentificationCalc}</td>
                                    <td className="text-right">
                                        {i?.amountNetC
                                            ? formatPrice(i.amountNetC)
                                            : '---'}
                                    </td>
                                    <td className="text-right">
                                        {i?.amountGrossC
                                            ? formatPrice(i.amountGrossC)
                                            : '---'}
                                    </td>
                                    <td>
                                        <span className="badge bg-primary text-white py-2">
                                            {i?.paidStateIdCalcDescription}
                                        </span>
                                    </td>
                                    <td>
                                        <Button
                                            size="sm"
                                            disabled={rowsInProgress.includes(
                                                i?.RID!
                                            )}
                                            outline
                                            onClick={() =>
                                                i?.RID &&
                                                downloadOrderDocument(i?.RID)
                                            }
                                        >
                                            {rowsInProgress.includes(
                                                i?.RID!
                                            ) ? (
                                                <div className="flex justify-center mx-4">
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                </div>
                                            ) : (
                                                t(
                                                    'eshop.usersettings.invoice.download'
                                                )
                                            )}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    className="mt-10"
                    current={page}
                    total={nextPage.current ? page + 1 : page}
                    onPageChange={(p) => {
                        setPageK2(
                            p < page ? prevPage.current : nextPage.current
                        )
                        setPage(p)
                    }}
                    hideNumbers
                />
            </Loader>
        </div>
    )
}
